import React, { useState, useCallback, useEffect } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Progress,
  Alert,
  AlertIcon,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react'
import { useDropzone } from 'react-dropzone'
import { FiUpload, FiFile, FiX, FiFolder, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { Folder, FileItem } from '@/types'
import { processFolderSelection } from '@/utils/folderProcessor'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useAppDispatch } from '@/store'
import { startFileUploads, startFolderUpload } from '@/store/thunks/uploadThunks'

interface UploadModalProps {
  isOpen: boolean
  onClose: () => void
  targetFolder?: Folder | null
  /** Tab to land on when the modal opens: 0 files, 1 folder. */
  initialTabIndex?: 0 | 1
}

/** Only a selection list now — progress is reported by the upload panel. */
interface UploadFile {
  file: File
  progress: number
  status: 'pending' | 'uploading' | 'completed' | 'failed'
  error?: string
  result?: FileItem
}

export default function UploadModal({
  isOpen,
  onClose,
  targetFolder,
  initialTabIndex = 0,
}: UploadModalProps) {
  const dispatch = useAppDispatch()
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([])
  const [folderFiles, setFolderFiles] = useState<FileList | null>(null)
  const [tabIndex, setTabIndex] = useState<number>(initialTabIndex)
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set())
  const toast = useToast()
  const { t, formatFileSize: formatFileSizeI18n } = useAppTranslation(['files', 'common'])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map((file) => ({
      file,
      progress: 0,
      status: 'pending' as const,
    }))
    setUploadFiles((prev) => [...prev, ...newFiles])
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.bmp'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: true,
  })

  const removeFile = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index))
  }

  /**
   * The modal only picks files. Uploading, duplicate prompts and progress are
   * owned by the upload panel, so hand off and get out of the way.
   */
  const handleUpload = () => {
    if (tabIndex === 0 && uploadFiles.length === 0) return
    if (tabIndex === 1 && !folderFiles) return

    if (tabIndex === 0) {
      void dispatch(
        startFileUploads({
          files: uploadFiles.map((entry) => entry.file),
          folderId: targetFolder?.id ?? null,
          folderName: targetFolder?.name ?? null,
        })
      )
    } else {
      void dispatch(
        startFolderUpload({
          files: folderFiles!,
          parentFolderId: targetFolder?.id ?? null,
        })
      )
    }

    handleClose()
  }

  const handleClose = () => {
    setUploadFiles([])
    setFolderFiles(null)
    onClose()
  }

  const clearCompletedFiles = () => {
    setUploadFiles((prev) => prev.filter((f) => f.status !== 'completed'))
  }

  // Clear files when modal opens. The tab is re-seeded here rather than only on mount: the
  // modal stays mounted between openings, so a caller that opens it straight onto "Folder"
  // would otherwise get whichever tab was left behind last time.
  useEffect(() => {
    if (isOpen) {
      setUploadFiles([])
      setFolderFiles(null)
      setTabIndex(initialTabIndex)
      setExpandedFolders(new Set())
    }
  }, [isOpen, initialTabIndex])

  // Expand all folders by default when folder is selected
  useEffect(() => {
    if (folderFiles) {
      const tree = buildFileTree()
      const allFolderPaths = new Set<string>()

      const collectFolderPaths = (node: any, path = '') => {
        Object.keys(node).forEach((key) => {
          if (!key.startsWith('_')) {
            const folderPath = path ? `${path}/${key}` : key
            allFolderPaths.add(folderPath)
            collectFolderPaths(node[key], folderPath)
          }
        })
      }

      collectFolderPaths(tree)
      setExpandedFolders(allFolderPaths)
    }
  }, [folderFiles])

  const allCompleted =
    uploadFiles.length > 0 &&
    uploadFiles.every((f) => f.status === 'completed' || f.status === 'failed')

  const hasCompletedFiles = uploadFiles.some((f) => f.status === 'completed')

  const getFolderFileCount = () => {
    if (!folderFiles) return 0
    return Array.from(folderFiles).length
  }

  // Check if a file type is supported
  const isFileSupported = (fileName: string, fileType: string) => {
    const supportedTypes = {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.bmp'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    }

    const extension = `.${fileName.split('.').pop()?.toLowerCase()}`

    return Object.entries(supportedTypes).some(([mimeType, extensions]) => {
      // Check MIME type match
      if (fileType && fileType.match(mimeType.replace('*', '.*'))) {
        return true
      }
      // Check extension match
      return extensions.includes(extension)
    })
  }

  const getFolderStructurePreview = () => {
    if (!folderFiles) return []

    const files = Array.from(folderFiles)
    const structure: { [key: string]: number } = {}

    files.forEach((file) => {
      const path = (file as any).webkitRelativePath || file.name
      const dir = path.split('/')[0]
      structure[dir] = (structure[dir] || 0) + 1
    })

    return Object.entries(structure)
  }

  // Build detailed file tree structure for preview
  const buildFileTree = () => {
    if (!folderFiles) return {}

    const files = Array.from(folderFiles)
    const tree: any = {}

    files.forEach((file) => {
      const path = (file as any).webkitRelativePath || file.name
      const parts = path.split('/')

      let current = tree

      // Build nested structure
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i]

        if (i === parts.length - 1) {
          // This is a file
          if (!current._files) current._files = []
          current._files.push({
            name: part,
            size: file.size,
            type: file.type,
            isSupported: isFileSupported(part, file.type),
          })
        } else {
          // This is a folder
          if (!current[part]) {
            current[part] = {}
          }
          current = current[part]
        }
      }
    })

    return tree
  }

  const toggleFolder = (folderPath: string) => {
    const newExpanded = new Set(expandedFolders)
    if (newExpanded.has(folderPath)) {
      newExpanded.delete(folderPath)
    } else {
      newExpanded.add(folderPath)
    }
    setExpandedFolders(newExpanded)
  }

  // Render file tree recursively
  const renderFileTree = (tree: any, level = 0, parentPath = '') => {
    const items: JSX.Element[] = []

    // Render folders first
    Object.keys(tree).forEach((key) => {
      if (key.startsWith('_')) return // Skip metadata keys

      const folderPath = parentPath ? `${parentPath}/${key}` : key
      const isCurrentlyExpanded = level === 0 || expandedFolders.has(folderPath)

      items.push(
        <Box key={folderPath}>
          <HStack
            spacing={1}
            py={1}
            px={2}
            ml={level * 4}
            cursor="pointer"
            _hover={{ bg: 'gray.50' }}
            borderRadius="sm"
            onClick={() => toggleFolder(folderPath)}
          >
            {isCurrentlyExpanded ? (
              <FiChevronDown size="12px" color="gray" />
            ) : (
              <FiChevronRight size="12px" color="gray" />
            )}
            <FiFolder size="14px" color="#4A90E2" />
            <Text fontSize="sm" fontWeight="medium">
              {key}
            </Text>
          </HStack>

          {isCurrentlyExpanded && <Box>{renderFileTree(tree[key], level + 1, folderPath)}</Box>}
        </Box>
      )
    })

    // Render files
    if (tree._files) {
      tree._files.forEach((file: any, index: number) => {
        items.push(
          <HStack key={`file-${parentPath}-${index}`} spacing={2} py={1} px={2} ml={level * 4}>
            {file.isSupported ? (
              <FiFile size="14px" color="gray" />
            ) : (
              <FiX size="14px" color="red" />
            )}
            <Text
              fontSize="sm"
              color={file.isSupported ? 'gray.700' : 'red.500'}
              flex={1}
              // textDecoration={file.isSupported ? "none" : "line-through"}
              noOfLines={1}
            >
              {file.name}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {formatFileSizeI18n(file.size)}
            </Text>
          </HStack>
        )
      })
    }

    return items
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent borderRadius="xl">
        <ModalHeader
          background="linear-gradient(135deg, var(--chakra-colors-brand-500), var(--chakra-colors-brand-700))"
          color="white"
          borderTopRadius="xl"
          borderBottom="1px solid"
          borderColor="brand.600"
        >
          {t('upload.uploadFiles', { ns: 'files' })}
          {targetFolder && (
            <span
              style={{ fontSize: '14px', fontWeight: 'normal', color: 'rgba(255,255,255,0.8)' }}
            >
              {' '}
              {t('upload.toFolder', { ns: 'files', folderName: targetFolder.name })}
            </span>
          )}
        </ModalHeader>
        <ModalCloseButton color="white" _hover={{ bg: 'whiteAlpha.200' }} />

        <ModalBody>
          <Tabs index={tabIndex} onChange={setTabIndex}>
            <TabList>
              <Tab>
                <HStack spacing={2}>
                  <FiFile />
                  <Text>{t('upload.uploadFiles', { ns: 'files' })}</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack spacing={2}>
                  <FiFolder />
                  <Text>{t('upload.uploadFolder', { ns: 'files' })}</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0} h="400px">
                <VStack spacing={4} h="full">
                  {/* File Drop Zone */}
                  <Box
                    {...getRootProps()}
                    border="2px dashed"
                    borderColor={isDragActive ? 'brand.500' : 'gray.300'}
                    borderRadius="md"
                    p={8}
                    textAlign="center"
                    cursor="pointer"
                    bg={isDragActive ? 'brand.50' : 'gray.50'}
                    transition="all 0.2s"
                    w="full"
                    flex={uploadFiles.length > 0 ? 'none' : '1'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <input {...getInputProps()} />
                    <VStack spacing={2}>
                      <FiUpload size="32px" color="gray" />
                      <Text fontWeight="medium">
                        {isDragActive
                          ? t('upload.dropFilesHere', { ns: 'files' })
                          : t('upload.dragDropOrClick', { ns: 'files' })}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {t('upload.supportedFileTypes', { ns: 'files' })}
                      </Text>
                    </VStack>
                  </Box>

                  {/* File List */}
                  {uploadFiles.length > 0 && (
                    <VStack spacing={2} w="full" flex={1} minH={0}>
                      <Text fontSize="sm" fontWeight="medium" alignSelf="flex-start" flexShrink={0}>
                        {t('upload.filesToUpload', { ns: 'files', count: uploadFiles.length })}
                      </Text>

                      <Box w="full" flex={1} minH={0} overflowY="auto">
                        {uploadFiles.map((uploadFile, index) => (
                          <Box
                            key={index}
                            w="full"
                            p={3}
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            mb={2}
                          >
                            <HStack spacing={3}>
                              <FiFile />
                              <VStack spacing={1} flex={1} align="stretch">
                                <HStack justify="space-between">
                                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                                    {uploadFile.file.name}
                                  </Text>
                                  {uploadFile.status === 'pending' && (
                                    <Button
                                      size="xs"
                                      variant="ghost"
                                      onClick={() => removeFile(index)}
                                    >
                                      <FiX />
                                    </Button>
                                  )}
                                </HStack>

                                {uploadFile.status === 'uploading' && (
                                  <Progress
                                    value={uploadFile.progress}
                                    size="sm"
                                    colorScheme="brand"
                                    hasStripe
                                  />
                                )}

                                {uploadFile.status === 'failed' && (
                                  <Alert status="error" size="sm">
                                    <AlertIcon />
                                    <Text fontSize="xs">{uploadFile.error}</Text>
                                  </Alert>
                                )}

                                {uploadFile.status === 'completed' && (
                                  <Text fontSize="xs" color="green.500">
                                    ✓ {t('upload.uploadedSuccessfully', { ns: 'files' })}
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                          </Box>
                        ))}
                      </Box>
                    </VStack>
                  )}
                </VStack>
              </TabPanel>

              <TabPanel px={0} h="400px">
                <HStack spacing={4} align="stretch" h="full">
                  {/* Left Column - Folder Selection */}
                  <VStack spacing={4} flex={1} align="stretch" h="full">
                    {/* Folder Drop Zone */}
                    <Box
                      border="2px dashed"
                      borderColor="gray.300"
                      borderRadius="md"
                      p={6}
                      textAlign="center"
                      w="full"
                      flex="1"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <VStack spacing={4}>
                        <FiFolder size="32px" color="gray" />
                        <Text fontWeight="medium">
                          {t('upload.selectFolderToUpload', { ns: 'files' })}
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          {t('upload.folderUploadDescription', { ns: 'files' })}
                        </Text>

                        <Button
                          size="sm"
                          colorScheme="brand"
                          variant="outline"
                          onClick={async () => {
                            try {
                              // Use shared folder processing utility
                              const fileList = await processFolderSelection()

                              if (fileList) {
                                // Modern API succeeded
                                setFolderFiles(fileList)

                                // Show feedback about files found
                                toast({
                                  title: t('upload.folderProcessed', { ns: 'files' }),
                                  description: t('upload.foundFilesInFolder', {
                                    ns: 'files',
                                    count: fileList.length,
                                  }),
                                  status: 'info',
                                  duration: 3000,
                                  isClosable: true,
                                })
                              } else {
                                // Fallback to traditional file input
                                const input = document.createElement('input')
                                input.type = 'file'
                                input.webkitdirectory = true
                                input.multiple = true
                                input.onchange = (event) => {
                                  const target = event.target as HTMLInputElement
                                  if (target.files) {
                                    setFolderFiles(target.files)
                                  }
                                }
                                input.click()
                              }
                            } catch (error) {
                              console.error('Error selecting folder:', error)
                              toast({
                                title: t('upload.folderSelectionFailed', { ns: 'files' }),
                                description: t('upload.folderSelectionErrorDescription', {
                                  ns: 'files',
                                }),
                                status: 'error',
                                duration: 5000,
                                isClosable: true,
                              })
                            }
                          }}
                        >
                          {t('upload.chooseFolder', { ns: 'files' })}
                        </Button>
                      </VStack>
                    </Box>

                    {/* Info Alert */}
                    {folderFiles && (
                      <Alert status="info" size="sm">
                        <AlertIcon />
                        <Text fontSize="xs">
                          {t('upload.unsupportedFilesSkipped', { ns: 'files' })}
                        </Text>
                      </Alert>
                    )}
                  </VStack>

                  {/* Right Column - File Structure Preview */}
                  <VStack spacing={3} flex={1} align="stretch" h="full">
                    <Box
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                      h="full"
                      bg="gray.50"
                    >
                      {folderFiles ? (
                        <Box
                          h="full"
                          overflowY="auto"
                          css={{
                            '&::-webkit-scrollbar': {
                              width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: '#f1f1f1',
                              borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: '#c1c1c1',
                              borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                              background: '#a8a8a8',
                            },
                          }}
                        >
                          <VStack spacing={0} align="stretch">
                            <Text fontSize="xs" color="gray.600" mb={2}>
                              {(() => {
                                if (!folderFiles)
                                  return t('upload.filesTotal', { ns: 'files', count: 0 })
                                const files = Array.from(folderFiles)
                                const supportedCount = files.filter((f) =>
                                  isFileSupported(f.name, f.type)
                                ).length
                                const totalCount = files.length
                                const unsupportedCount = totalCount - supportedCount
                                return unsupportedCount > 0
                                  ? t('upload.filesSupportedUnsupported', {
                                      ns: 'files',
                                      supported: supportedCount,
                                      unsupported: unsupportedCount,
                                      total: totalCount,
                                    })
                                  : t('upload.filesTotal', { ns: 'files', count: totalCount })
                              })()}
                            </Text>
                            {renderFileTree(buildFileTree())}
                          </VStack>
                        </Box>
                      ) : (
                        <VStack spacing={4} justify="center" h="full" color="gray.500">
                          <FiFolder size="48px" />
                          <VStack spacing={2}>
                            <Text fontSize="sm" fontWeight="medium">
                              {t('upload.noFolderSelected', { ns: 'files' })}
                            </Text>
                            <Text fontSize="xs" textAlign="center">
                              {t('upload.chooseFolderToSeeStructure', { ns: 'files' })}
                              <br />
                              {t('upload.previewShowsFiles', { ns: 'files' })}
                              <br />
                              {t('upload.thatWillBeUploaded', { ns: 'files' })}
                            </Text>
                          </VStack>
                        </VStack>
                      )}
                    </Box>
                  </VStack>
                </HStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter
          borderTop="1px solid"
          borderColor="gray.200"
          bg="gray.50"
          borderBottomRadius="xl"
        >
          <HStack spacing={3} w="full" justify="space-between">
            <Box>
              {hasCompletedFiles && tabIndex === 0 && (
                <Button size="sm" variant="ghost" colorScheme="gray" onClick={clearCompletedFiles}>
                  {t('upload.clearCompleted', { ns: 'files' })}
                </Button>
              )}
            </Box>

            <HStack spacing={3}>
              <Button variant="ghost" onClick={handleClose}>
                {allCompleted
                  ? t('buttons.close', { ns: 'common' })
                  : t('buttons.cancel', { ns: 'common' })}
              </Button>

              {!allCompleted && (
                <Button
                  colorScheme="brand"
                  onClick={handleUpload}
                  loadingText={t('upload.uploading', { ns: 'files' })}
                  isDisabled={
                    (tabIndex === 0 && uploadFiles.length === 0) || (tabIndex === 1 && !folderFiles)
                  }
                >
                  {tabIndex === 0
                    ? t('upload.uploadFilesWithCount', { ns: 'files', count: uploadFiles.length })
                    : t('upload.uploadFolderWithCount', {
                        ns: 'files',
                        count: getFolderFileCount(),
                      })}
                </Button>
              )}
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
